import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';

type IncidentAgency = {
  id?: UUID;
  incident_id: UUID;
  agency_name?: string;
  representative_name?: string;
};

export const keys = {
  base: 'agencies',
  get all() {
    return [keys.base];
  },
  get: function (group_guid: UUID, user_guid: UUID) {
    return [keys.base, group_guid, user_guid];
  },
} as const;

export const useIncidentAgencies = (incidentId: string) => {
  return useQuery({
    queryKey: ['agencies', incidentId],
    queryFn: async () => {
      const resp = await axios.get<IncidentAgency[]>(
        `${config.apiGateway.ics_incident_agencies}/${incidentId}`
      );
      return resp.data;
    },
    enabled: !!incidentId,
    throwOnError: () => {
      return false;
    },
  });
};

export const useCreateIncidentAgency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newIncidentAgency: IncidentAgency) => {
      const resp = await axios.post<IncidentAgency>(
        `${config.apiGateway.ics_incident_agencies}`,
        newIncidentAgency
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useUpdateIncidentAgency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updatedIncidentAgency: IncidentAgency) => {
      const resp = await axios.put<IncidentAgency>(
        `${config.apiGateway.ics_incident_agencies}/${updatedIncidentAgency.id}`,
        updatedIncidentAgency
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useDeleteIncidentAgency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID) => {
      await axios.delete(`${config.apiGateway.ics_incident_agencies}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};
